import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useToast } from './Toast';
import {
  canShowRentalPaymentLink,
  getConditionHoldAmount,
  getRentalPaymentAmount,
  MAX_CONDITION_GUARANTEE,
} from './rentalPayments';

const stripePromise = loadStripe(
  'pk_live_51RMKJrCFHNvMneAiSTiC3q61oC08dIMQy4MtSoCfBeYKKCTvjaxWGqgnelPb0mQJF8YwtXQj5LTCsOtNdBL8k8PP009WzivPGE'
);

const CHECKOUT_URL =
  'https://us-central1-attackyouradventure-ee7a6.cloudfunctions.net/createCheckoutSession';

export function PayButton({
  amount,
  gearName,
  rentalData,
  fullWidth = true,
  style = {},
  disabled = false,
}) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const displayAmount = Number(amount);

  const handlePayment = async () => {
    const amountInCents = Math.round(displayAmount * 100);

    if (!Number.isFinite(amountInCents) || amountInCents < 100) {
      showToast('Unable to start payment. The rental total must be at least $1.00.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(CHECKOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amountInCents,
          gearName,
          rentalData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to create checkout session');
      }

      const data = await response.json();

      if (data.url) {
        window.location.assign(data.url);
        return;
      }

      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId: data.id });

      if (error) {
        showToast(error.message, 'error');
      }
    } catch (error) {
      console.error('Payment error:', error);
      showToast(`Payment failed: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handlePayment}
      disabled={loading || disabled}
      style={{
        width: fullWidth ? '100%' : 'auto',
        padding: '12px 22px',
        backgroundColor: loading || disabled ? '#9ca3af' : '#10b981',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontWeight: '700',
        marginTop: '12px',
        cursor: loading || disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {loading ? 'Processing...' : `Save Card — $${displayAmount.toFixed(2)}`}
    </button>
  );
}

export function BookingPaymentBlock({ rental, gear = null }) {
  const gearRef = gear || { pricePerDay: rental.pricePerDay };
  if (!canShowRentalPaymentLink(rental, gearRef)) return null;

  const amount = getRentalPaymentAmount(rental, gearRef);
  const conditionHoldAmount = getConditionHoldAmount(gearRef, rental);

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '14px 16px',
        borderRadius: '10px',
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
      }}
    >
      <p style={{ margin: '0 0 8px 0', color: '#166534', fontSize: '0.92rem', lineHeight: '1.5' }}>
        Save your card to lock in these dates. Your card will be charged 48 hours before the rental starts (or immediately if you book inside that window).
        {conditionHoldAmount > 0 && (
          <>
            {' '}
            After the charge, a condition guarantee of up to ${MAX_CONDITION_GUARANTEE.toLocaleString()} may be authorized on your card.
          </>
        )}
      </p>
      <PayButton
        amount={amount}
        gearName={rental.gearName || 'Gear Rental'}
        rentalData={{
          gearId: rental.gearId,
          rentalId: rental.id,
          startDate: rental.rentalStartDate,
          endDate: rental.rentalEndDate,
        }}
        fullWidth={false}
        style={{ marginTop: '8px' }}
      />
    </div>
  );
}

export default PayButton;